import {
  computed,
  inject,
  Injectable,
  signal,
  WritableSignal,
} from '@angular/core';
import { AuthService } from '@services/auth.service';
import { WebSocketService } from '@services/web-socket.service';
import { TopicType } from '../enums/topic-type';
import { PatientRequest } from '@interfaces/data/patient-request';
import { ShiftRequest } from '@interfaces/data/shift-request';
import { Shift } from '@interfaces/data/shift';
import { WeekDay } from '@features/staff-scheduler/staff-scheduler.component';
import { WeekDayAssignment } from '@interfaces/shift/week-day-assignment';
import { ShiftAssignmentRequest } from '@interfaces/shift/shift-assignment-request';
import { ManageAssignmentRequest } from '@interfaces/shift/manage-assignment-request';
import { AssignmentStore } from '@stores/assignment.store';
import { AssignmentPatient } from '@interfaces/assignment/assignment-patient';
import { StaffKey } from '@interfaces/assignment/staff-key';
import { PageInfo } from '@interfaces/page-info/page-info';
import { ShiftService } from '@services/shift.service';

@Injectable({
  providedIn: 'root',
})
export class AssignmentService {
  constructor(
    private authService: AuthService,
    private webSocketService: WebSocketService,
    private shiftService: ShiftService,
  ) {}
  private assignmentStore = inject(AssignmentStore);

  patients = computed(() => {
    return this.assignmentStore
      .patients()
      .slice()
      .sort(
        (a, b) => (a.assignments?.length ?? 0) - (b.assignments?.length ?? 0),
      );
  });
  staffKeys = this.assignmentStore.staffKey as WritableSignal<StaffKey[]>;
  pageInfo = signal<undefined | PageInfo>(undefined);
  redirectToDashboard = this.assignmentStore
    .redirectToDashboard as WritableSignal<boolean>;
  selectedShiftId = this.assignmentStore
    .selectedShiftId as WritableSignal<string>;
  selectedPatientId = this.assignmentStore
    .selectedPatientId as WritableSignal<string>;

  setData(data: { patients: AssignmentPatient[]; staffKey: StaffKey[] }) {
    this.assignmentStore.setAssignments(data);
  }

  sendSecurityRequest(
    shiftGroups: {
      shiftNumber: number;
      startTime: string;
      endTime: string;
      department: string;
      id?: string;
      monday: WeekDayAssignment[];
      tuesday: WeekDayAssignment[];
      wednesday: WeekDayAssignment[];
      thursday: WeekDayAssignment[];
      friday: WeekDayAssignment[];
      saturday: WeekDayAssignment[];
      sunday: WeekDayAssignment[];
    }[],
    updatedShift?: Shift,
  ) {
    const userId = this.authService.user().id;

    const shifts: ShiftAssignmentRequest[] = shiftGroups.map((s) => {
      let shiftToUpdate = {
        shiftNumber: s.shiftNumber,
        startTime: s.startTime,
        endTime: s.endTime,
      };
      if (updatedShift && updatedShift.shiftNumber === s.shiftNumber) {
        shiftToUpdate = this.mapShiftToRequest(updatedShift);
      }
      const mapAssignment = (assignment: WeekDayAssignment) => assignment.id;
      return {
        shift: {
          shiftNumber: shiftToUpdate.shiftNumber,
          startTime: shiftToUpdate.startTime,
          endTime: shiftToUpdate.endTime,
        },
        assignments: {
          [WeekDay.MONDAY]: s.monday.map(mapAssignment),
          [WeekDay.TUESDAY]: s.tuesday.map(mapAssignment),
          [WeekDay.WEDNESDAY]: s.wednesday.map(mapAssignment),
          [WeekDay.THURSDAY]: s.thursday.map(mapAssignment),
          [WeekDay.FRIDAY]: s.friday.map(mapAssignment),
          [WeekDay.SATURDAY]: s.saturday.map(mapAssignment),
          [WeekDay.SUNDAY]: s.sunday.map(mapAssignment),
        },
      };
    });

    const message: ManageAssignmentRequest = {
      requester: userId!,
      type: 'security',
      data: shifts,
    };
    this.webSocketService.send({
      topic: TopicType.MANAGE_ASSIGNMENT_REQUEST,
      message,
    });
  }

  updateAssignmentsAndShift(
    patients: PatientRequest[],
    shift: Shift,
    department: string,
  ) {
    const shiftRequest = this.mapShiftToRequest(shift, department);
    const userId = this.authService.user().id;
    const message: object = {
      requester: userId,
      assignments: patients,
      shift: shiftRequest,
    };
    this.webSocketService.send({
      topic: TopicType.MANAGE_ASSIGNMENT_REQUEST,
      message,
    });
    this.shiftService.updateShift(shift);
  }

  private mapShiftToRequest(shift: Shift, department?: string): ShiftRequest {
    const mapToStringTime = (inputTime: { hour: number; minute: number }) => {
      const hour =
        `${inputTime.hour}`.length === 1
          ? `0${inputTime.hour}`
          : inputTime.hour;
      const minute =
        `${inputTime.minute}`.length === 1
          ? `0${inputTime.minute}`
          : inputTime.minute;
      return `${hour}:${minute}`;
    };
    return {
      ...shift,
      department: department,
      startTime: mapToStringTime(shift.startTime),
      endTime: mapToStringTime(shift.endTime),
    };
  }

  setPageInfo(pageInfo: PageInfo) {
    this.pageInfo.set(pageInfo);
  }

  setRedirectToDashboard(redirect: boolean) {
    this.assignmentStore.setRedirectToDashboard(redirect);
  }

  setSelectedShiftId(id: string) {
    this.assignmentStore.setSelectedShiftId(id);
  }

  setSelectedPatientId(id: string) {
    this.assignmentStore.setSelectedPatientId(id);
  }
}
